import type { ChangeEvent, FormEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../../api/client';
import {
  fetchPatientPaymentInvoiceBlob,
  isPdfPaymentInvoiceFile,
  isSupportedPaymentInvoiceFile,
  PAYMENT_INVOICE_ACCEPT,
  PAYMENT_INVOICE_MAX_BYTES,
  PAYMENT_INVOICE_PDF_ACCEPT,
  type AdminPatientPaymentResponse,
  type PaymentMethod,
} from '../../../api/patient-payments';
import { useAuth } from '../../auth/AuthProvider';
import { InvoiceDropZone } from './InvoiceDropZone';
import { StatusChip } from '../../../components/StatusChip';
import { useActionDialog } from '../../../components/ui';
import { useHasPermission } from '../../auth/usePermission';
import { useBankAccounts } from '../hooks/useBankAccounts';
import { usePatientPoints } from '../hooks/usePatientPoints';
import {
  useCreatePatientPayment,
  useDeletePatientPayment,
  useDeletePatientPaymentInvoice,
  useListPatientPayments,
  useSetPatientPaymentInvoice,
  useUpdatePatientPayment,
} from '../hooks/usePatientPayments';
import type { BankAccount } from '../../../types/admin';
import styles from './PatientPaymentsPanel.module.scss';

type PatientPaymentsPanelProps = {
  readonly patientId: string;
};

const PAYMENT_METHODS: readonly PaymentMethod[] = ['bank', 'link', 'invoice_pdf'];

function parsePaymentMethod(value: string): PaymentMethod {
  return PAYMENT_METHODS.find((method) => method === value) ?? 'bank';
}

/** Method of an existing payment; older API responses derive it from the requisites. */
function paymentMethodOf(payment: AdminPatientPaymentResponse): PaymentMethod {
  if (payment.payment_method) return payment.payment_method;
  if (payment.payment_link) return 'link';
  if (payment.bank_account_id) return 'bank';
  return 'invoice_pdf';
}

/** Whether the attached invoice can serve as the only requisite (must be a PDF). */
function hasPdfInvoice(payment: AdminPatientPaymentResponse): boolean {
  return Boolean(payment.invoice_url) && /\.pdf$/i.test(payment.invoice_url ?? '');
}

type CreateFormState = {
  readonly title: string;
  readonly payment_method: PaymentMethod;
  readonly bank_account_id: string;
  readonly payment_link: string;
  readonly amount: string;
  readonly currency: string;
  readonly deadline: string;
  readonly mark_paid: boolean;
};

const INITIAL_CREATE_FORM: CreateFormState = {
  title: '',
  payment_method: 'bank',
  bank_account_id: '',
  payment_link: '',
  amount: '',
  currency: 'UAH',
  deadline: '',
  mark_paid: false,
};

function isoToDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatBankAccount(account: BankAccount | undefined): string {
  if (!account) return '—';
  return `${account.account_name} — ${account.iban} (${account.bank_name})`;
}

/** Translation key for why an invoice file is refused, or null when it is fine. */
function invoiceRejectionKey(file: File, method: PaymentMethod): string | null {
  if (!isSupportedPaymentInvoiceFile(file)) return 'clientDetail.payments.invoiceWrongType';
  if (method === 'invoice_pdf' && !isPdfPaymentInvoiceFile(file)) {
    return 'clientDetail.payments.invoicePdfOnly';
  }
  if (file.size > PAYMENT_INVOICE_MAX_BYTES) return 'clientDetail.payments.invoiceTooLarge';
  return null;
}

function invoiceAcceptFor(method: PaymentMethod): string {
  return method === 'invoice_pdf' ? PAYMENT_INVOICE_PDF_ACCEPT : PAYMENT_INVOICE_ACCEPT;
}

/**
 * Requisite fields for the create/update payload, keyed by the chosen method.
 *
 * Bank and link payments keep the legacy shape (the server infers the method
 * from the requisite); only `invoice_pdf` has to be named explicitly because
 * it carries no requisite field at all.
 */
function requisitesPayload(
  method: PaymentMethod,
  bankAccountId: string,
  paymentLink: string,
): { bank_account_id?: string; payment_link?: string; payment_method?: PaymentMethod } {
  if (method === 'bank') return { bank_account_id: bankAccountId };
  if (method === 'link') return { payment_link: paymentLink };
  return { payment_method: 'invoice_pdf' };
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

export function PatientPaymentsPanel({ patientId }: PatientPaymentsPanelProps) {
  const { t, i18n } = useTranslation();
  const { tokens } = useAuth();
  const { data: payments, isLoading: paymentsLoading } = useListPatientPayments(patientId);
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts();
  const createMutation = useCreatePatientPayment(patientId);
  const updateMutation = useUpdatePatientPayment(patientId);
  const setInvoiceMutation = useSetPatientPaymentInvoice(patientId);
  const deleteInvoiceMutation = useDeletePatientPaymentInvoice(patientId);
  const deletePaymentMutation = useDeletePatientPayment(patientId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(INITIAL_CREATE_FORM);
  const [createInvoiceFile, setCreateInvoiceFile] = useState<File | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const bankAccountById = useMemo(() => {
    const map = new Map<string, BankAccount>();
    (bankAccounts ?? []).forEach((acc) => map.set(acc.id, acc));
    return map;
  }, [bankAccounts]);

  function formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function setRowErrorFor(paymentId: string, message: string | null) {
    setRowError((prev) => {
      const next = { ...prev };
      if (message === null) {
        delete next[paymentId];
      } else {
        next[paymentId] = message;
      }
      return next;
    });
  }

  async function handleCreateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreateError(null);
    const amount = parseFloat(createForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const trimmedLink = createForm.payment_link.trim();
    if (createForm.payment_method === 'bank' && !createForm.bank_account_id) return;
    if (createForm.payment_method === 'link' && !trimmedLink) return;
    if (createForm.payment_method === 'invoice_pdf' && !createInvoiceFile) {
      setCreateError(t('clientDetail.payments.invoicePdfRequired'));
      return;
    }
    try {
      const createdPayment = await createMutation.mutateAsync({
        ...(createForm.title.trim() ? { title: createForm.title.trim() } : {}),
        ...requisitesPayload(createForm.payment_method, createForm.bank_account_id, trimmedLink),
        amount,
        currency: createForm.currency.trim().toUpperCase() || 'UAH',
        ...(createForm.deadline ? { deadline: new Date(createForm.deadline).toISOString() } : {}),
        ...(createForm.mark_paid ? { mark_paid: true } : {}),
      });
      if (createInvoiceFile) {
        try {
          await setInvoiceMutation.mutateAsync({
            paymentId: createdPayment.id,
            file: createInvoiceFile,
          });
        } catch {
          setRowErrorFor(
            createdPayment.id,
            t('clientDetail.payments.uploadAfterCreateFailed'),
          );
        }
      }
      setCreateForm(INITIAL_CREATE_FORM);
      setCreateInvoiceFile(null);
      setIsCreateOpen(false);
    } catch (error) {
      setCreateError(extractErrorMessage(error, t('clientDetail.payments.genericError')));
    }
  }

  function toggleCreateForm() {
    setCreateError(null);
    if (isCreateOpen) setCreateInvoiceFile(null);
    setIsCreateOpen((prev) => !prev);
  }

  function applyCreateInvoiceFile(file: File | null) {
    setCreateError(null);
    if (!file) {
      setCreateInvoiceFile(null);
      return;
    }
    const rejectionKey = invoiceRejectionKey(file, createForm.payment_method);
    if (rejectionKey) {
      setCreateInvoiceFile(null);
      setCreateError(t(rejectionKey));
      return;
    }
    setCreateInvoiceFile(file);
  }

  function changeCreatePaymentMethod(rawValue: string) {
    const method = parsePaymentMethod(rawValue);
    setCreateError(null);
    setCreateForm((prev) => ({ ...prev, payment_method: method }));
    // A Word/Excel file picked earlier cannot stand in for the PDF requisite.
    if (method === 'invoice_pdf' && createInvoiceFile && !isPdfPaymentInvoiceFile(createInvoiceFile)) {
      setCreateInvoiceFile(null);
      setCreateError(t('clientDetail.payments.invoicePdfOnly'));
    }
  }

  const createIsPdfMethod = createForm.payment_method === 'invoice_pdf';

  return (
    <div className={styles.panel}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarHints}>
          <p className={styles.workflowHint}>
            {t('clientDetail.payments.cycleAutoSwitchHint')}
          </p>
          <PointsBalanceHint patientId={patientId} />
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={toggleCreateForm}
          disabled={createMutation.isPending || setInvoiceMutation.isPending}
          aria-expanded={isCreateOpen}
          aria-controls="create-patient-payment"
        >
          {isCreateOpen ? t('clientDetail.payments.cancel') : t('clientDetail.issueInvoice')}
        </button>
      </div>

      {isCreateOpen ? (
        <form
          id="create-patient-payment"
          className={styles.createForm}
          onSubmit={handleCreateSubmit}
          aria-label={t('clientDetail.issueInvoice')}
        >
          <h4 className={styles.formTitle}>{t('clientDetail.issueInvoice')}</h4>
          <div className={styles.formGrid}>
            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>{t('clientDetail.payments.title')}</span>
              <input
                type="text"
                value={createForm.title}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder={t('clientDetail.payments.titlePlaceholder')}
                maxLength={255}
              />
            </label>
            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>{t('clientDetail.payments.paymentMethod')}</span>
              <select
                value={createForm.payment_method}
                onChange={(e) => changeCreatePaymentMethod(e.target.value)}
              >
                <option value="bank">{t('clientDetail.payments.paymentMethodBank')}</option>
                <option value="link">{t('clientDetail.payments.paymentMethodLink')}</option>
                <option value="invoice_pdf">
                  {t('clientDetail.payments.paymentMethodInvoicePdf')}
                </option>
              </select>
            </label>
            {createIsPdfMethod ? (
              <p className={`${styles.fieldFull} ${styles.workflowHint}`}>
                {t('clientDetail.payments.invoicePdfMethodHint')}
              </p>
            ) : createForm.payment_method === 'bank' ? (
              <label className={`${styles.field} ${styles.fieldFull}`}>
                <span>{t('clientDetail.payments.bankAccount')}</span>
                {bankAccountsLoading ? (
                  <select disabled>
                    <option>{t('clientDetail.payments.loading')}</option>
                  </select>
                ) : (
                  <select
                    value={createForm.bank_account_id}
                    required
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, bank_account_id: e.target.value }))
                    }
                  >
                    <option value="">{t('clientDetail.payments.selectBank')}</option>
                    {bankAccounts?.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {formatBankAccount(acc)}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            ) : (
              <label className={`${styles.field} ${styles.fieldFull}`}>
                <span>{t('clientDetail.payments.paymentLink')}</span>
                <input
                  type="url"
                  value={createForm.payment_link}
                  required
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, payment_link: e.target.value }))
                  }
                  placeholder={t('clientDetail.payments.paymentLinkPlaceholder')}
                />
              </label>
            )}
            <label className={styles.field}>
              <span>{t('clientDetail.payments.amount')}</span>
              <input
                type="number"
                value={createForm.amount}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                step="0.01"
                min="0.01"
                required
              />
            </label>
            <label className={styles.field}>
              <span>{t('clientDetail.payments.currency')}</span>
              <select
                value={createForm.currency}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, currency: e.target.value }))}
              >
                <option value="UAH">UAH</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
            <label className={styles.field}>
              <span>{t('clientDetail.payments.deadline')}</span>
              <input
                type="datetime-local"
                value={createForm.deadline}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, deadline: e.target.value }))}
                disabled={createForm.mark_paid}
              />
            </label>
            <label className={styles.checkboxField}>
              <input
                type="checkbox"
                checked={createForm.mark_paid}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, mark_paid: e.target.checked }))
                }
              />
              <span className={styles.checkboxText}>
                <span className={styles.checkboxLabel}>
                  {t('clientDetail.payments.markPaidLabel')}
                </span>
                <span className={styles.checkboxHint}>
                  {t('clientDetail.payments.markPaidHint')}
                </span>
              </span>
            </label>
            <InvoiceDropZone
              label={
                createIsPdfMethod
                  ? t('clientDetail.payments.invoiceFilePdf')
                  : t('clientDetail.payments.invoiceFile')
              }
              hint={
                createIsPdfMethod
                  ? t('clientDetail.payments.invoiceFilePdfHint')
                  : t('clientDetail.payments.invoiceFileHint')
              }
              dropHint={t('clientDetail.payments.invoiceDropHint')}
              emptyLabel={t('clientDetail.payments.invoiceNoFile')}
              clearLabel={t('clientDetail.payments.invoiceRemoveFile')}
              file={createInvoiceFile}
              accept={invoiceAcceptFor(createForm.payment_method)}
              disabled={createMutation.isPending || setInvoiceMutation.isPending}
              captureDocumentPaste
              onFilePicked={applyCreateInvoiceFile}
            />
          </div>
          {createError ? <p className={styles.inlineError}>{createError}</p> : null}
          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={
                createMutation.isPending || setInvoiceMutation.isPending
              }
            >
              {createMutation.isPending || setInvoiceMutation.isPending
                ? t('clientDetail.payments.creating')
                : createForm.mark_paid
                ? t('clientDetail.payments.recordPrepayment')
                : t('clientDetail.payments.create')}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={toggleCreateForm}
              disabled={createMutation.isPending || setInvoiceMutation.isPending}
            >
              {t('clientDetail.payments.cancel')}
            </button>
          </div>
        </form>
      ) : null}

      {paymentsLoading ? (
        <div className={styles.loadingList} role="status" aria-label={t('clientDetail.payments.loading')}>
          <span className={styles.loadingCard} />
          <span className={styles.loadingCard} />
        </div>
      ) : !payments || payments.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyStateIcon} aria-hidden="true">₴</span>
          <p>{t('clientDetail.noPaymentsYet')}</p>
        </div>
      ) : (
        <div className={styles.list}>
          {payments.map((payment) => (
            <PaymentRow
              key={payment.id}
              patientId={patientId}
              payment={payment}
              bankAccount={
                payment.bank_account_id
                  ? bankAccountById.get(payment.bank_account_id)
                  : undefined
              }
              bankAccounts={bankAccounts ?? []}
              bankAccountsLoading={bankAccountsLoading}
              tokenAvailable={Boolean(tokens?.access_token)}
              accessToken={tokens?.access_token ?? null}
              formatDateTime={formatDateTime}
              updateMutation={updateMutation}
              setInvoiceMutation={setInvoiceMutation}
              deleteInvoiceMutation={deleteInvoiceMutation}
              deletePaymentMutation={deletePaymentMutation}
              error={rowError[payment.id] ?? null}
              setError={(msg) => setRowErrorFor(payment.id, msg)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type PaymentRowProps = {
  readonly patientId: string;
  readonly payment: AdminPatientPaymentResponse;
  readonly bankAccount: BankAccount | undefined;
  readonly bankAccounts: readonly BankAccount[];
  readonly bankAccountsLoading: boolean;
  readonly tokenAvailable: boolean;
  readonly accessToken: string | null;
  readonly formatDateTime: (value: string | null | undefined) => string;
  readonly updateMutation: ReturnType<typeof useUpdatePatientPayment>;
  readonly setInvoiceMutation: ReturnType<typeof useSetPatientPaymentInvoice>;
  readonly deleteInvoiceMutation: ReturnType<typeof useDeletePatientPaymentInvoice>;
  readonly deletePaymentMutation: ReturnType<typeof useDeletePatientPayment>;
  readonly error: string | null;
  readonly setError: (message: string | null) => void;
};

function PaymentRow({
  patientId,
  payment,
  bankAccount,
  bankAccounts,
  bankAccountsLoading,
  tokenAvailable,
  accessToken,
  formatDateTime,
  updateMutation,
  setInvoiceMutation,
  deleteInvoiceMutation,
  deletePaymentMutation,
  error,
  setError,
}: PaymentRowProps) {
  const { t } = useTranslation();
  const { confirmAction, promptForText } = useActionDialog();
  const canEditDetails =
    payment.payment_status === 'pending' || payment.payment_status === 'rejected';
  const canConfirmReject = payment.payment_status === 'submitted_by_patient';

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    title: string;
    payment_method: PaymentMethod;
    bank_account_id: string;
    payment_link: string;
    amount: string;
    deadline: string;
  }>({
    title: payment.title ?? '',
    payment_method: paymentMethodOf(payment),
    bank_account_id: payment.bank_account_id ?? '',
    payment_link: payment.payment_link ?? '',
    amount: String(payment.amount),
    deadline: isoToDateTimeLocal(payment.deadline),
  });
  const [isMutating, setIsMutating] = useState(false);
  const paymentMethod = paymentMethodOf(payment);
  const isPdfMethod = paymentMethod === 'invoice_pdf';

  function resetEditForm() {
    setEditForm({
      title: payment.title ?? '',
      payment_method: paymentMethodOf(payment),
      bank_account_id: payment.bank_account_id ?? '',
      payment_link: payment.payment_link ?? '',
      amount: String(payment.amount),
      deadline: isoToDateTimeLocal(payment.deadline),
    });
  }

  function startEdit() {
    resetEditForm();
    setError(null);
    setIsEditing(true);
  }

  function cancelEdit() {
    setIsEditing(false);
    setError(null);
  }

  async function handleSaveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amount = parseFloat(editForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const trimmedLink = editForm.payment_link.trim();
    if (editForm.payment_method === 'bank' && !editForm.bank_account_id) return;
    if (editForm.payment_method === 'link' && !trimmedLink) return;
    if (editForm.payment_method === 'invoice_pdf' && !hasPdfInvoice(payment)) {
      setError(t('clientDetail.payments.invoicePdfMethodNeedsFile'));
      return;
    }
    setError(null);
    setIsMutating(true);
    try {
      await updateMutation.mutateAsync({
        paymentId: payment.id,
        payload: {
          title: editForm.title.trim() ? editForm.title.trim() : null,
          ...requisitesPayload(editForm.payment_method, editForm.bank_account_id, trimmedLink),
          amount,
          ...(editForm.deadline ? { deadline: new Date(editForm.deadline).toISOString() } : {}),
        },
      });
      setIsEditing(false);
    } catch (err) {
      setError(extractErrorMessage(err, t('clientDetail.payments.genericError')));
    } finally {
      setIsMutating(false);
    }
  }

  async function handleInvoiceFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    const rejectionKey = invoiceRejectionKey(file, paymentMethod);
    if (rejectionKey) {
      setError(t(rejectionKey));
      return;
    }
    setIsMutating(true);
    try {
      await setInvoiceMutation.mutateAsync({ paymentId: payment.id, file });
    } catch (err) {
      setError(extractErrorMessage(err, t('clientDetail.payments.genericError')));
    } finally {
      setIsMutating(false);
    }
  }

  async function handleDeleteInvoice() {
    if (!(await confirmAction({
      message: t('clientDetail.payments.invoiceDeleteConfirm'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) return;
    setError(null);
    setIsMutating(true);
    try {
      await deleteInvoiceMutation.mutateAsync(payment.id);
    } catch (err) {
      setError(extractErrorMessage(err, t('clientDetail.payments.genericError')));
    } finally {
      setIsMutating(false);
    }
  }

  async function handleViewInvoice() {
    if (!accessToken) return;
    setError(null);
    try {
      const blob = await fetchPatientPaymentInvoiceBlob(patientId, payment.id, accessToken);
      const url = URL.createObjectURL(blob);
      if (blob.type === 'application/pdf') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = payment.invoice_original_filename || 'invoice';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(extractErrorMessage(err, t('clientDetail.payments.genericError')));
    }
  }

  async function handleConfirm() {
    setError(null);
    setIsMutating(true);
    try {
      await updateMutation.mutateAsync({
        paymentId: payment.id,
        payload: { payment_status: 'confirmed' },
      });
    } catch (err) {
      setError(extractErrorMessage(err, t('clientDetail.payments.genericError')));
    } finally {
      setIsMutating(false);
    }
  }

  async function handleDeletePayment() {
    if (!(await confirmAction({
      message: t('clientDetail.payments.deletePaymentConfirm'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) return;
    setError(null);
    setIsMutating(true);
    try {
      await deletePaymentMutation.mutateAsync(payment.id);
    } catch (err) {
      setError(extractErrorMessage(err, t('clientDetail.payments.genericError')));
    } finally {
      setIsMutating(false);
    }
  }

  async function handleReject() {
    const reason = await promptForText({
      title: t('clientDetail.payments.rejectCta'),
      message: t('clientDetail.payments.rejectReasonPrompt'),
      inputLabel: t('clientDetail.payments.rejectionReason'),
      confirmLabel: t('clientDetail.payments.rejectCta'),
      tone: 'danger',
      required: true,
    });
    if (!reason) return;
    setError(null);
    setIsMutating(true);
    try {
      await updateMutation.mutateAsync({
        paymentId: payment.id,
        payload: { payment_status: 'rejected', rejection_reason: reason },
      });
    } catch (err) {
      setError(extractErrorMessage(err, t('clientDetail.payments.genericError')));
    } finally {
      setIsMutating(false);
    }
  }

  return (
    <article className={styles.paymentCard} aria-busy={isMutating}>
      <header className={styles.paymentHeader}>
        <div className={styles.listItemInfo}>
          <span className={styles.listItemTitle}>
            {payment.title || t('clientDetail.payments.untitledInvoice')}
          </span>
          <strong className={styles.paymentAmount}>
            {payment.amount} {payment.currency}
          </strong>
          {paymentMethod === 'link' ? (
            <span className={styles.listItemMeta}>
              {t('clientDetail.payments.paymentLink')}:{' '}
              <a
                href={payment.payment_link ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('clientDetail.payments.openPaymentLink')}
              >
                {payment.payment_link}
              </a>
            </span>
          ) : isPdfMethod ? (
            <span className={styles.listItemMeta}>
              {t('clientDetail.payments.paymentMethod')}:{' '}
              {t('clientDetail.payments.paymentMethodInvoicePdf')}
              {payment.invoice_original_filename ? ` · ${payment.invoice_original_filename}` : ''}
            </span>
          ) : (
            <span className={styles.listItemMeta}>
              {t('clientDetail.payments.bankAccount')}: {formatBankAccount(bankAccount)}
            </span>
          )}
          <span className={styles.listItemMeta}>
            {t('clientDetail.payments.deadline')}: {formatDateTime(payment.deadline)} ·{' '}
            {t('clientDetail.payments.createdAt')}: {formatDateTime(payment.created_at)}
          </span>
          {payment.rejection_reason ? (
            <span className={styles.listItemMeta}>
              {t('clientDetail.payments.rejectionReason')}: {payment.rejection_reason}
            </span>
          ) : null}
        </div>
        <StatusChip status={payment.payment_status} />
      </header>

      <div className={styles.listItemActions}>
        {payment.invoice_url ? (
          <>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={handleViewInvoice}
              disabled={!tokenAvailable || isMutating}
            >
              {t('clientDetail.payments.viewInvoice')}
            </button>
            {canEditDetails ? (
              <>
                <label className={styles.fileButton}>
                  {t('clientDetail.payments.replaceInvoice')}
                  <input
                    type="file"
                    accept={invoiceAcceptFor(paymentMethod)}
                    onChange={handleInvoiceFileChange}
                    disabled={isMutating}
                    className={styles.visuallyHiddenInput}
                  />
                </label>
                {/* The PDF is the only requisite of an invoice_pdf payment; the server refuses to drop it. */}
                {isPdfMethod ? null : (
                  <button
                    type="button"
                    className={styles.dangerBtn}
                    onClick={handleDeleteInvoice}
                    disabled={isMutating}
                  >
                    {t('clientDetail.payments.deleteInvoice')}
                  </button>
                )}
              </>
            ) : null}
          </>
        ) : canEditDetails ? (
          <label className={styles.fileButton}>
            {t('clientDetail.payments.uploadInvoice')}
            <input
              type="file"
              accept={invoiceAcceptFor(paymentMethod)}
              onChange={handleInvoiceFileChange}
              disabled={isMutating}
              className={styles.visuallyHiddenInput}
            />
          </label>
        ) : null}

        {canEditDetails ? (
          <>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={isEditing ? cancelEdit : startEdit}
              disabled={isMutating}
            >
              {isEditing ? t('clientDetail.payments.cancel') : t('clientDetail.payments.edit')}
            </button>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={handleDeletePayment}
              disabled={isMutating}
            >
              {t('clientDetail.payments.deletePayment')}
            </button>
          </>
        ) : null}

        {canConfirmReject ? (
          <>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleConfirm}
              disabled={isMutating}
            >
              {t('clientDetail.payments.confirmCta')}
            </button>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={handleReject}
              disabled={isMutating}
            >
              {t('clientDetail.payments.rejectCta')}
            </button>
          </>
        ) : null}
      </div>

      {isEditing ? (
        <form className={styles.editForm} onSubmit={handleSaveEdit}>
          <div className={styles.formGrid}>
            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>{t('clientDetail.payments.title')}</span>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder={t('clientDetail.payments.titlePlaceholder')}
                maxLength={255}
              />
            </label>
            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>{t('clientDetail.payments.paymentMethod')}</span>
              <select
                value={editForm.payment_method}
                onChange={(e) => {
                  const method = parsePaymentMethod(e.target.value);
                  setError(null);
                  setEditForm((prev) => ({ ...prev, payment_method: method }));
                }}
              >
                <option value="bank">{t('clientDetail.payments.paymentMethodBank')}</option>
                <option value="link">{t('clientDetail.payments.paymentMethodLink')}</option>
                <option value="invoice_pdf">
                  {t('clientDetail.payments.paymentMethodInvoicePdf')}
                </option>
              </select>
            </label>
            {editForm.payment_method === 'invoice_pdf' ? (
              <p className={`${styles.fieldFull} ${styles.workflowHint}`}>
                {hasPdfInvoice(payment)
                  ? t('clientDetail.payments.invoicePdfMethodHint')
                  : t('clientDetail.payments.invoicePdfMethodNeedsFile')}
              </p>
            ) : editForm.payment_method === 'bank' ? (
              <label className={`${styles.field} ${styles.fieldFull}`}>
                <span>{t('clientDetail.payments.bankAccount')}</span>
                <select
                  value={editForm.bank_account_id}
                  required
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, bank_account_id: e.target.value }))
                  }
                  disabled={bankAccountsLoading}
                >
                  <option value="">{t('clientDetail.payments.selectBank')}</option>
                  {bankAccounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {formatBankAccount(acc)}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className={`${styles.field} ${styles.fieldFull}`}>
                <span>{t('clientDetail.payments.paymentLink')}</span>
                <input
                  type="url"
                  value={editForm.payment_link}
                  required
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, payment_link: e.target.value }))
                  }
                  placeholder={t('clientDetail.payments.paymentLinkPlaceholder')}
                />
              </label>
            )}
            <label className={styles.field}>
              <span>{t('clientDetail.payments.amount')}</span>
              <input
                type="number"
                value={editForm.amount}
                onChange={(e) => setEditForm((prev) => ({ ...prev, amount: e.target.value }))}
                step="0.01"
                min="0.01"
                required
              />
            </label>
            <label className={styles.field}>
              <span>{t('clientDetail.payments.deadline')}</span>
              <input
                type="datetime-local"
                value={editForm.deadline}
                onChange={(e) => setEditForm((prev) => ({ ...prev, deadline: e.target.value }))}
              />
            </label>
          </div>
          <div className={styles.actions}>
            <button type="submit" className={styles.primaryBtn} disabled={isMutating}>
              {t('clientDetail.payments.saveEdit')}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={cancelEdit}
              disabled={isMutating}
            >
              {t('clientDetail.payments.cancel')}
            </button>
          </div>
        </form>
      ) : null}

      {error ? <p className={styles.inlineError} role="alert">{error}</p> : null}
    </article>
  );
}

/**
 * Shows the patient's bonus balance next to the invoice form.
 *
 * Points never move a price on their own — the admin decides how much of the
 * balance to honour and types the reduced amount below — so the balance has to
 * be visible at exactly the moment the amount is entered.
 */
function PointsBalanceHint({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation();
  const canView = useHasPermission('points:view');
  if (!canView) return null;
  return <PointsBalanceHintInner patientId={patientId} label={t} />;
}

function PointsBalanceHintInner({
  patientId,
  label,
}: {
  readonly patientId: string;
  readonly label: (key: string, options?: Record<string, unknown>) => string;
}) {
  const { data } = usePatientPoints(patientId);
  if (!data || data.balance_points <= 0) return null;
  return (
    <p className={styles.workflowHint}>
      {label('clientDetail.payments.pointsBalanceHint', {
        points: data.balance_points,
        amount: data.balance_uah,
        currency: data.currency,
      })}
    </p>
  );
}
